/**
 * @file
 * Creates a modal for flagging a message and also a singleton that initializes
 * and opens said modal.
 */

/* eslint
   no-script-url: 0
*/

import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";

import _str from "shared/util/_str";
import OkC from "shared/util/OkcApi/index";
import Analytics from "analytics/util/Analytics";
import FeedbackBanner from "shared/util/FeedbackBanner";
import * as ProfileActions from "shared/util/ProfileActions";
import Report, {
	TYPES,
	TYPE_IDS,
	TITLES,
	REASONS,
} from "shared/util/Report";

import Str from "shared/components/Str";
import Strf from "shared/components/Strf";
import Modal from "shared/components/Modal";
import Radio from "shared/components/oknf/OkRadio";
import Textarea from "shared/components/oknf/OkTextarea";

import "./ModalReport.scss";

const labelsRequiringDescription = [
	"4", // Underage
	"5", // Assault / Abusive / Violent
];

export const MODAL_ID = "report_modal";

class ReportModal extends Component {
	state = {
		comment: "",
		showSumbissionError: false,
		showFlagmod: false,
		showPhotoDetails: false,
		label: null,
		isSubmitting: false,
	};

	_close = () => {
		Report.close();
	};

	_togglePhotoDetails = () => {
		this.setState({ showPhotoDetails: !this.state.showPhotoDetails });
	};

	_flagMessage = () => {
		const { label, comment } = this.state;
		const {
			userid,
			matchPercentage,
			threadid,
			msgid,
			fromContentWarning,
		} = this.props;

		const flagData = {
			type: "message",
			label,
			comment,
			id: threadid || msgid,
			auto: fromContentWarning,
		};

		const analyticsData = {
			userid,
			label,
			description: comment,
			messageId: msgid,
			threadId: threadid,
			matchPercentage,
		};

		this._flag(flagData, analyticsData);
	};

	_flagProfile = () => {
		const {
			userid,
			matchPercentage,
		} = this.props;
		const { label, comment } = this.state;

		const flagData = {
			userid,
			id: userid,
			type: TYPE_IDS[TYPES.PROFILE],
			label,
			comment,
		};

		const analyticsData = {
			userid,
			label,
			description: comment,
			matchPercentage,
		};

		this._flag(flagData, analyticsData);
	};

	_flagPhoto = ({ isAlbum }) => {
		const {
			userid,
			photoId,
			matchPercentage,
		} = this.props;
		const {
			label,
			comment,
		} = this.state;

		const type = isAlbum ? TYPE_IDS[TYPES.PHOTO_ALBUM] : TYPE_IDS[TYPES.PHOTO];

		const flagData = {
			userid,
			source: "photo",
			type,
			label,
			comment,
			id: photoId,
		};

		let reason = "";

		if (window.location && window.location.pathname.indexOf("/profile/") >= 0) {
			reason = "flagged from profile.";
		} else if (window.location && window.location.pathname.indexOf("/doubletake") >= 0) {
			reason = "reported on quickmatch";
		}

		const analyticsData = {
			userid,
			reason,
			description: comment,
			photoId,
			matchPercentage,
		};

		this._flag(flagData, analyticsData);
	};

	_flag = (flagData, analyticsData) => {
		const { type, userid, matchPercentage } = this.props;

		this.setState({ isSubmitting: true });

		OkC.post(`/profile/${userid}/report`, {
			data: flagData,
			success: (res) => {
				this.setState({ isSubmitting: false });

				Analytics.logUIEvent(Analytics.eventNames.profileReport, {
					data: analyticsData,
				});

				if (type !== TYPES.MESSAGE && res && res.flagmod_eligible) {
					this.setState({ showFlagmod: true });
				} else {
					FeedbackBanner.open({
						type: "success",
						message: _str("Report submitted! Retribution will be swift."),
						timeout: 4000,
					});

					this._close();

					if (type === TYPES.MESSAGE) {
						ProfileActions.block(userid, {
							skipModal: true,
							matchPercentage,
						});
					}
				}
			},
			error: () => {
				this.setState({
					isSubmitting: false,
					showSumbissionError: true,
				});
			},
		});
	};

	_handleSubmit = (e) => {
		e.preventDefault();

		const { type } = this.props;

		if (type === TYPES.MESSAGE) {
			this._flagMessage();
		} else if (type === TYPES.PROFILE) {
			this._flagProfile();
		} else if (type === TYPES.PHOTO) {
			this._flagPhoto({ isAlbum: false });
		} else if (type === TYPES.PHOTO_ALBUM) {
			this._flagPhoto({ isAlbum: true });
		}
	};

	_setDescription = (e) => {
		this.setState({ comment: e.target.value });
	};

	_setLabel = (label) => {
		return () => {
			this.setState({ label });
		};
	}

	render() {
		const { type } = this.props;
		const {
			showSumbissionError,
			showFlagmod,
			showPhotoDetails,
			isSubmitting,
			label,
			comment,
		} = this.state;

		if (showSumbissionError) {
			return (
				<Modal
					title={TITLES[type]}
					onCancel={this._close}
					show
				>
					<Str>Something went wrong. Please refresh and try again.</Str>
				</Modal>
			);
		}

		if (showFlagmod) {
			return (
				<Modal
					id={MODAL_ID}
					title={TITLES[type]}
					onCancel={this._close}
					show
				>
					<h2>
						<Str>Report submitted!</Str>
					</h2>
					<p className="native-forms-section">
						<Str>
							Our moderators will look into this shortly. Thanks for helping out.
						</Str>
					</p>
					<p className="native-forms-section">
						<Str>
							And guess what else? You’re currently eligible to be a flag moderator.
							Help OkCupid out by voting on flags set by other people.
						</Str>
					</p>
					<p className="native-forms-section">
						<a href="/flagmod" className="flatbutton flagmod">
							<Str>Check out Flagmod</Str>
						</a>
						<button className="flatbutton gray" onClick={this._close}>
							<Str>Maybe later</Str>
						</button>
					</p>
				</Modal>
			);
		}

		const labelIsHighRisk = labelsRequiringDescription.indexOf(label) !== -1;
		const submittable = !!label && (labelIsHighRisk ? !!comment : true);

		return (
			<Modal
				id={MODAL_ID}
				title={TITLES[type]}
				onCancel={this._close}
				show
			>
				<form className="oknf oknf--gray" onSubmit={this._handleSubmit}>
					<div className="native-forms-section">
						<div className="report-modal-description">
							{type === TYPES.MESSAGE &&
								<Str>Why are you reporting this message?</Str>
							}

							{type === TYPES.PROFILE &&
								<Strf
									vars={{
										termsOfService: (
											<a href="https://okcupid.com/legal/terms" target="_blank" rel="noopener noreferrer">
												{_str("terms of service")}
											</a>
										),
										communityGuidelines: (
											<a href="https://help.okcupid.com/article/142-community-guidelines" target="_blank" rel="noopener noreferrer">
												{_str("community guidelines")}
											</a>
										),
									}}
								>
									How does this violate our $termsOfService or $communityGuidelines?
								</Strf>
							}

							{type === TYPES.PHOTO &&
								<Fragment>
									<Strf vars={{
										photoRules: (
											<button
												className="photo-details-button"
												onClick={this._togglePhotoDetails}
												type="button"
											>
												<Str>Photo Rules</Str>
											</button>
										),
									}}>
										How does this violate our $photoRules?
									</Strf>
									{showPhotoDetails &&
										<Strf
											className="photo-details"
											vars={{
												here: (
													<a href="https://help.okcupid.com/article/147-photo-rules" target="_blank" rel="noopener noreferrer">
														<Str>here</Str>
													</a>
												),
											}}
										>
											You must be in the photo! Full nudity, extreme close ups,
											pets, cars, baby photos, artwork, images you’ve added
											yourself to, etc. are not allowed. See full photo rules
											$here.
										</Strf>
									}
								</Fragment>
							}

							{type === TYPES.PHOTO_ALBUM &&
								<Str>
									Photos in albums are meant to showcase other aspects of your
									life, and may include pets, cars, baby photos, artwork, etc.
									Full nudity and offensive content are not allowed.
								</Str>
							}
						</div>

						{REASONS[type].map((reason) => (
							<Radio
								key={reason.text}
								name="reportReason"
								label={reason.text}
								onChange={this._setLabel(reason.value)}
								checked={label === reason.value}
								value={reason.value}
							/>
						))}

					</div>

					<div className="native-forms-section">
						<label className="oknf-label" htmlFor="description">
							{labelIsHighRisk &&
								<Str>Description (Required)</Str>
							}
							{!labelIsHighRisk &&
								<Str>Description</Str>
							}
						</label>

						<Textarea
							id="description"
							value={comment}
							onChange={this._setDescription}
						/>
					</div>

					<button
						className="flatbutton blue"
						type="submit"
						disabled={!submittable || isSubmitting}
					>
						{type === TYPES.PROFILE &&
							<Str>Report them!</Str>
						}
						{type !== TYPES.PROFILE &&
							<Str>Report it!</Str>
						}
					</button>

					<button
						className="flatbutton silver"
						type="button"
						onClick={this._close}
						disabled={isSubmitting}
					>
						<Str>Cancel</Str>
					</button>
				</form>
			</Modal>
		);
	}
}

ReportModal.propTypes = {
	userid: PropTypes.string.isRequired,
	msgid: PropTypes.string,
	threadid: PropTypes.string,
	photoId: PropTypes.string,
	fromContentWarning: PropTypes.bool,
	matchPercentage: PropTypes.number,
	type: PropTypes.string,
};

ReportModal.defaultProps = {
	msgid: null,
	threadid: null,
	photoId: null,
	fromContentWarning: false,
	matchPercentage: 0,
	type: "message",
};

export default ReportModal;



// WEBPACK FOOTER //
// ./src/shared/components/ModalReport.jsx